import {
    GuideDetailsContent,
    RequiredGuideMediaStatusBadges,
} from '@fruitful/guidance';
import { useAtomValue } from 'jotai';
import { Column } from 'primereact/column';
import {
    DataTable,
    type DataTableRowEditCompleteEvent,
    type DataTableRowEditEvent,
} from 'primereact/datatable';
import React, { useState } from 'react';
import {
    BadgeAction,
    BodyMediumEmphasized,
    BodyMediumRegular,
    Button,
    ButtonVariant,
    H6Regular,
    LabelRegular,
    MonoLabelRegular,
    XStack,
    YStack,
} from 'smoothie';

import {
    DateTextEditor,
    DropdownEditor,
    TextAreaEditor,
    TextEditor,
} from '../../common/components/GridCellEditors';
import { GroveSimpleModal } from '../../common/components/GroveSimpleModal';
import {
    formatToSimpleDateString,
    formatToSimpleDateTimeString,
} from '../../common/utils/formatting';
import { adminUserAtom, allGuidesAtom } from '../../entities/entityAtoms';
import { type Guide, GuideStatus } from '../../entities/entityTypes';
import type { GuideTableRow } from '../types/GuideTypes';
import { GuideSelectionDropdown } from './GuideSelectionDropdown';

interface GuideTableProps {
    guides: GuideTableRow[];
    onCreateNew: () => void;
    onRowEditComplete: (e: DataTableRowEditCompleteEvent) => void;
}

const getStatusBadgeColor = (status: GuideStatus): string => {
    switch (status) {
        case GuideStatus.ACTIVE:
            return 'green';
        case GuideStatus.INACTIVE:
            return 'gray';
        case GuideStatus.LEFT_COMPANY:
            return 'red';
        default:
            return 'gray';
    }
};

// Status dropdown options
const statusOptions = [
    { label: 'Active', value: GuideStatus.ACTIVE },
    { label: 'Inactive', value: GuideStatus.INACTIVE },
    { label: 'Left Company', value: GuideStatus.LEFT_COMPANY },
];

const StatusRenderer = ({ status }: { status: GuideStatus }) => {
    const color = getStatusBadgeColor(status);
    return (
        <BodyMediumRegular color={color}>
            {status.replace('_', ' ')}
        </BodyMediumRegular>
    );
};

const OooDateRenderer = ({ date }: { date?: string | null }) => {
    return (
        <BodyMediumRegular>
            {formatToSimpleDateString(date, {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
            })}
        </BodyMediumRegular>
    );
};

const GuideProfileCell = ({ guide }: { guide: GuideTableRow }) => {
    return (
        <XStack alignItems="center" gap={8}>
            <img
                src={guide.profileImageUrl ?? ''}
                width={40}
                height={40}
                alt=""
                style={{ borderRadius: '50%', objectFit: 'cover' }}
            />
            <BodyMediumEmphasized>{guide.fullName}</BodyMediumEmphasized>
        </XStack>
    );
};

const GuideMediaStatusCell = ({
    guide,
    onPressViewDetails,
}: {
    guide: GuideTableRow;
    onPressViewDetails: () => void;
}) => {
    return (
        <YStack alignItems="flex-start" gap={2}>
            <RequiredGuideMediaStatusBadges guide={guide as Guide} />
            <BadgeAction
                variant="neutral"
                text="View details"
                onPress={onPressViewDetails}
            />
        </YStack>
    );
};

export const GuideTable = ({
    guides,
    onCreateNew,
    onRowEditComplete,
}: GuideTableProps) => {
    const adminUser = useAtomValue(adminUserAtom);
    const canEditGuides = adminUser.authorization.guides.canEditAll;
    const allGuides = useAtomValue(allGuidesAtom);
    const [editingRows, setEditingRows] = useState<Record<string, boolean>>({});

    const [isGuideDetailsModalOpen, setIsGuideDetailsModalOpen] =
        useState(false);
    const [guideForDetails, setGuideForDetails] =
        useState<GuideTableRow | null>(null);

    const onRowEditChange = (e: DataTableRowEditEvent) => {
        setEditingRows(e.data);
    };

    return (
        <YStack gap={16} maxWidth="100%">
            <XStack justifyContent="space-between" alignItems="center">
                <H6Regular>Guides</H6Regular>
                {canEditGuides && (
                    <Button
                        text="Create New Guide"
                        variant={ButtonVariant.PRIMARY}
                        onPress={onCreateNew}
                        width="auto"
                    />
                )}
            </XStack>

            <DataTable
                value={guides}
                size="small"
                showGridlines
                stripedRows
                scrollable
                scrollHeight="flex"
                emptyMessage="No guides found"
                editMode="row"
                dataKey="id"
                onRowEditChange={onRowEditChange}
                onRowEditComplete={onRowEditComplete}
                editingRows={editingRows}
            >
                <Column
                    field="id"
                    header="ID"
                    sortable
                    style={{ minWidth: '100px' }}
                    body={({ id }: GuideTableRow) => {
                        return <MonoLabelRegular>{id}</MonoLabelRegular>;
                    }}
                />
                <Column
                    frozen
                    field="fullName"
                    header="Name"
                    sortable
                    body={(guide: GuideTableRow) => {
                        return <GuideProfileCell guide={guide} />;
                    }}
                />
                <Column
                    field="signUpId"
                    header="Sign Up ID"
                    style={{ minWidth: '100px' }}
                    editor={(options) => TextEditor({ options })}
                />
                <Column
                    field="email"
                    header="Email"
                    sortable
                    style={{ minWidth: '200px' }}
                />
                <Column
                    field="status"
                    header="Status"
                    sortable
                    style={{ minWidth: '120px' }}
                    body={(guide: GuideTableRow) => (
                        <StatusRenderer status={guide.status} />
                    )}
                    editor={(options) => (
                        <DropdownEditor
                            options={options}
                            dropdownProps={{ options: statusOptions }}
                        />
                    )}
                />
                <Column
                    field="advisorId"
                    header="Advisor"
                    style={{ minWidth: '150px' }}
                    body={({ advisorId }: GuideTableRow) => {
                        return (
                            allGuides.find((guide) => guide.id === advisorId)
                                ?.fullName ?? ''
                        );
                    }}
                    editor={(options) =>
                        GuideSelectionDropdown({
                            options,
                            guides: allGuides,
                        })
                    }
                />
                <Column
                    field="pronouns"
                    header="Pronouns"
                    style={{ minWidth: '100px' }}
                    editor={(options) => TextEditor({ options })}
                />
                <Column
                    field="profileImageUrl"
                    header="Profile Image URL"
                    style={{
                        minWidth: '120px',
                        maxWidth: '240px',
                        wordWrap: 'break-word',
                    }}
                    editor={(options) => TextAreaEditor({ options })}
                    body={({ profileImageUrl }: GuideTableRow) => {
                        return (
                            <MonoLabelRegular>
                                {profileImageUrl}
                            </MonoLabelRegular>
                        );
                    }}
                />
                <Column
                    field="sendbirdUserId"
                    header="Sendbird User ID"
                    style={{ minWidth: '150px' }}
                    editor={(options) => TextEditor({ options })}
                    body={({ sendbirdUserId }: GuideTableRow) => {
                        return (
                            <MonoLabelRegular>
                                {sendbirdUserId}
                            </MonoLabelRegular>
                        );
                    }}
                />
                <Column
                    field="slackId"
                    header="Slack ID"
                    style={{ minWidth: '120px' }}
                    body={({ slackId }: GuideTableRow) => {
                        return <MonoLabelRegular>{slackId}</MonoLabelRegular>;
                    }}
                />
                <Column
                    field="firefliesApiKey"
                    header="Fireflies API Key"
                    style={{ minWidth: '120px' }}
                    editor={(options) => TextEditor({ options })}
                    body={({ firefliesApiKey }: GuideTableRow) => {
                        return (
                            <MonoLabelRegular>
                                {firefliesApiKey}
                            </MonoLabelRegular>
                        );
                    }}
                />
                <Column
                    field="oooStart"
                    header="OOO Start"
                    style={{ minWidth: '150px' }}
                    body={(guide: GuideTableRow) => (
                        <OooDateRenderer date={guide.oooStart} />
                    )}
                    editor={(options) => (
                        <DateTextEditor
                            options={options}
                            calendarProps={{
                                minDate: new Date('2024-01-01'),
                                placeholder: 'MM/DD/YYYY',
                            }}
                        />
                    )}
                />
                <Column
                    field="oooEnd"
                    header="OOO End"
                    style={{ minWidth: '150px' }}
                    body={(guide: GuideTableRow) => (
                        <OooDateRenderer date={guide.oooEnd} />
                    )}
                    editor={(options) => (
                        <DateTextEditor
                            options={options}
                            calendarProps={{
                                minDate: new Date('2024-01-01'),
                                placeholder: 'MM/DD/YYYY',
                            }}
                        />
                    )}
                />
                <Column
                    field="bio"
                    header="Bio"
                    style={{ minWidth: '600px', maxWidth: '800px' }}
                    editor={(options) => TextAreaEditor({ options })}
                    body={({ bio }: GuideTableRow) => {
                        return <LabelRegular>{bio}</LabelRegular>;
                    }}
                />
                <Column
                    field="styles"
                    header="Styles"
                    style={{ minWidth: '150px' }}
                    editor={(options) => TextAreaEditor({ options })}
                    body={({ styles }: GuideTableRow) => {
                        return <LabelRegular>{styles}</LabelRegular>;
                    }}
                />
                <Column
                    header="Bio Media"
                    style={{ minWidth: '140px' }}
                    body={(guide: GuideTableRow) => {
                        return (
                            <GuideMediaStatusCell
                                guide={guide}
                                onPressViewDetails={() => {
                                    setGuideForDetails(guide);
                                    setIsGuideDetailsModalOpen(true);
                                }}
                            />
                        );
                    }}
                />
                <Column
                    field="createdAt"
                    header="Created"
                    sortable
                    style={{ minWidth: '120px' }}
                    body={({ createdAt }: GuideTableRow) => {
                        return formatToSimpleDateTimeString(createdAt);
                    }}
                />
                <Column
                    field="updatedAt"
                    header="Updated"
                    sortable
                    style={{ minWidth: '120px' }}
                    body={({ updatedAt }: GuideTableRow) => {
                        return formatToSimpleDateTimeString(updatedAt);
                    }}
                />
                {canEditGuides && (
                    <Column rowEditor header="Edit" style={{ width: '7rem' }} />
                )}
            </DataTable>
            <GroveSimpleModal
                isOpen={isGuideDetailsModalOpen}
                onClose={() => {
                    setIsGuideDetailsModalOpen(false);
                    setGuideForDetails(null);
                }}
            >
                {guideForDetails ? (
                    <GuideDetailsContent
                        guide={guideForDetails as Guide}
                        containerHeight={500}
                        containerWidth={900}
                        onOpenChange={setIsGuideDetailsModalOpen}
                    />
                ) : null}
            </GroveSimpleModal>
        </YStack>
    );
};
